import { BookMarked, FileText, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoreEntryList } from "@/components/lore/lore-entry-list";
import { LoreFilterBar } from "@/components/lore/lore-filter-bar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
  LoreEntryFilters,
  LoreEntryView,
  LoreLimitPrompt,
  ProjectDocumentKind,
  ProjectDocumentView,
  ProjectLoreCapacitySummary,
} from "@/features/lore";
import { cn } from "@/lib/utils";

type LoreDocumentRailProps = {
  activeDocumentKind: ProjectDocumentKind | null;
  activeEntryId: string | null;
  allowCreate?: boolean;
  capacity: ProjectLoreCapacitySummary | null;
  documents: ProjectDocumentView[];
  entries: LoreEntryView[];
  filters: LoreEntryFilters;
  isCreatingEntry: boolean;
  limitPrompt: LoreLimitPrompt | null;
  onClearFilters: () => void;
  onCreateEntry: () => void;
  onOpenDocument: (kind: ProjectDocumentKind) => void;
  onOpenEntry: (entryId: string) => void;
  onSearchTextChange: (searchText: string) => void;
  onStatusFilterChange: (statusFilter: LoreEntryFilters["statusFilter"]) => void;
  onTypeFilterChange: (typeFilter: LoreEntryFilters["typeFilter"]) => void;
};

export function LoreDocumentRail({
  activeDocumentKind,
  activeEntryId,
  allowCreate = true,
  documents,
  entries,
  filters,
  isCreatingEntry,
  limitPrompt,
  onClearFilters,
  onCreateEntry,
  onOpenDocument,
  onOpenEntry,
  onSearchTextChange,
  onStatusFilterChange,
  onTypeFilterChange,
}: LoreDocumentRailProps) {
  const { t } = useTranslation();

  return (
    <aside className="min-h-[calc(100vh-8rem)] space-y-8 border-r border-ink/10 pr-6 xl:sticky xl:top-28 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-clay">
          {t("lore.workspace.eyebrow")}
        </p>
        <h2 className="font-display text-3xl leading-none tracking-tight text-foreground">
          {t("lore.workspace.documentsTitle")}
        </h2>
        <p className="text-sm leading-6 text-muted-foreground">
          {t("lore.workspace.sidebarBody")}
        </p>
      </div>

      <div className="space-y-2">
        {documents.map((document) => (
          <button
            key={document.id}
            className={cn(
              "flex w-full items-start gap-3 rounded-[1.35rem] border px-4 py-3 text-left transition-colors",
              document.kind === activeDocumentKind
                ? "border-clay/10 bg-[#efe2cf] text-foreground"
                : "border-transparent bg-transparent text-muted-foreground hover:bg-white/65 hover:text-foreground",
            )}
            onClick={() => onOpenDocument(document.kind)}
            type="button"
          >
            {document.kind === "governance"
              ? (
                <BookMarked className="mt-0.5 h-4 w-4" />
              )
              : (
                <FileText className="mt-0.5 h-4 w-4" />
              )}
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-sm font-medium text-foreground">
                {t(document.titleKey)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {document.badgeKeys.map((badgeKey) => (
                  <Badge
                    key={badgeKey}
                    variant="secondary"
                    className="rounded-full bg-white/75 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-clay shadow-none"
                  >
                    {t(badgeKey)}
                  </Badge>
                ))}
              </div>
            </div>
          </button>
        ))}
      </div>

      {allowCreate ? (
        <Button
          className={cn(
            "h-12 w-full justify-center rounded-2xl bg-clay text-canvas shadow-none hover:bg-clay/90",
            isCreatingEntry ? "bg-primary" : undefined,
          )}
          onClick={onCreateEntry}
          type="button"
          variant="default"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("lore.entries.newAction")}
        </Button>
      ) : null}

      {limitPrompt ? (
        <div className="rounded-[1.35rem] border border-clay/15 bg-clay/5 px-4 py-4">
          <p className="text-sm font-medium text-foreground">
            {t("lore.messages.limitReachedTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("lore.messages.limitReachedBody", {
              allowed: limitPrompt.allowedCount,
              tier: limitPrompt.tier,
            })}
          </p>
        </div>
      ) : null}

      <div className="space-y-5">
        <LoreFilterBar
          filters={filters}
          onClearFilters={onClearFilters}
          onSearchTextChange={onSearchTextChange}
          onStatusFilterChange={onStatusFilterChange}
          onTypeFilterChange={onTypeFilterChange}
        />

        <LoreEntryList
          activeEntryId={activeEntryId}
          entries={entries}
          onOpenEntry={onOpenEntry}
        />
      </div>
    </aside>
  );
}
